/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { useEffect, useMemo, useRef, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { getBscGlobalState, sendScreenEvent } from '../../../common/utils';
import { isSSR } from '../../../common/utils/is-ssr';
import {
	ATLASSIAN_BANNER_LAST_DISMISSED_TIME_COOKIE_KEY,
	ATLASSIAN_SHOW_BANNER_COOKIE_KEY,
} from '../../../constants';
import { shouldRenderBannerPrefetched } from '../../../controllers/banner-controls/should-banner-render-prefetched';
import type { PrefetchedConsentPreferenceData } from '../../../controllers/banner-controls/types';
import { setStrictlyNecessaryCookie } from '../../../controllers/cookie-controls';
import { getCookie } from '../../../controllers/cookie-controls/get-cookie';
import { initialPreferencesAreMissing } from '../../../controllers/cookie-controls/transformer';
import type { PrefetchedConsentPreferences } from '../../../services/consent-hub-service/types';
import { hasPrefetchedDataConsents } from '../../../services/consent-hub-service/utils';
import {
	PrefetchedPreferenceState,
	type ConsentPreference,
	type SSRFriendlyBannerOptions,
} from '../../../types';

export interface BannerVisibilityHookProps {
	preferences: ConsentPreference | undefined;
	isPrefetchedPreferencesState: boolean;
	initialPreferences?: PrefetchedConsentPreferences;
	isInitialRender: boolean;
	ssrOptions?: SSRFriendlyBannerOptions;
}

/**
 * @private
 * @deprecated Legacy SSR banner visibility check that doesn't consider fallbackConsentToken.
 * Kept only as the feature-gate-off fallback for 'platform_moonjelly_bsc_ssr_banner_flash_fix'.
 * Will be removed once the gate is fully rolled out.
 */
const shouldShowBannerInSSRContextLegacy = (
	initialPreferences?: PrefetchedConsentPreferences,
): boolean => {
	if (
		!initialPreferences ||
		(initialPreferences as unknown) === PrefetchedPreferenceState.INTERNAL ||
		initialPreferencesAreMissing(initialPreferences)
	) {
		return true;
	}
	return !hasPrefetchedDataConsents(initialPreferences);
};

export const calculateBannerStateViaBit = (
	isInitialRender: boolean,
	initialPreferences?: PrefetchedConsentPreferences,
	ssrOptions?: SSRFriendlyBannerOptions,
): boolean => {
	// We don't have a great way to handle dealing with consent timestamps right here, as we have no global state/store, so we have to precompute
	// banner visibility state when we fetch consents, and store it in a cookie to be read later, here.
	const shouldShowBannerBit = ssrOptions?.getCookie
		? ssrOptions.getCookie(ATLASSIAN_SHOW_BANNER_COOKIE_KEY)
		: getCookie(ATLASSIAN_SHOW_BANNER_COOKIE_KEY);

	const inFlowWhereBannerStateIsUnknown =
		initialPreferences && isInitialRender && shouldShowBannerBit === undefined;

	if ((ssrOptions?.isSSR ?? isSSR()) || inFlowWhereBannerStateIsUnknown) {
		// When coming from SSR, this hook will only execute once and won't update later
		// Instead, if we're on the server, have initial preference data with valid consents, don't render
		// On later checks (rehydration), this will always fail and skip to the normal rendering logic

		// Once we're rehydrating, if have been provided initial prefs (from SSR), on the "first render", the banner cookie may still be missing.
		// Due to React lifecycle where this useMemo is executing prior to the useEffect in the usePreferences hook, which sets the core cookies.
		// We should give this flow the same treatment as rendering in an SSR context to match the behavior of the SSR flow to reduce any potential UX flicker.

		if (initialPreferences === PrefetchedPreferenceState.INTERNAL) {
			// If we're in the "fetch internally" state, we have no prefetched data and we can't determine
			// if the user has consented or not yet since we need to fetch, so show the banner
			return true;
		}

		if (fg('platform_moonjelly_bsc_ssr_banner_flash_fix')) {
			// shouldRenderBannerPrefetched is the single source of truth for SSR/hydration banner visibility.
			// It is used here and by all external consumers (e.g. useCookiesConsentBanner) to guarantee
			// consistent decisions, including correct handling of fallbackConsentToken.
			//
			// Cast: PrefetchedConsentPreferenceData is a narrower type than PrefetchedConsentPreferences —
			// it excludes PrefetchedPreferenceState (handled above) but is otherwise compatible at this call site.
			return shouldRenderBannerPrefetched(
				initialPreferences as PrefetchedConsentPreferenceData | undefined,
				ssrOptions,
			);
		}

		// Legacy path: uses hasPrefetchedDataConsents which doesn't consider fallbackConsentToken.
		// This path will be removed once the feature gate is fully rolled out.
		return shouldShowBannerInSSRContextLegacy(initialPreferences);
	}

	const shouldShowBanner = shouldShowBannerBit ? !!Number(shouldShowBannerBit) : false;
	return shouldShowBanner;
};

/**
 * This hook is responsible for determining whether or not the consent banner should be shown based on the user's
 * preferences and the current state of the banner cookie, as well as if the we're rendering from an SSR context.
 *
 * @param preferences - The current state of the user's preferences
 * @param initialPreferences - Any initially provided preferences from consuming applications (used for SSR)
 * @param isInitialRender - A boolean to track if this is the "first" render of the component
 * @param isPrefetchedPreferencesState - A boolean that indicates if are fetching consents internally to our component
 * @returns showBanner - Whether or not the banner should be shown
 * @returns setIsDismissed - Setter function to update the dismissed state of the banner
 */
const useBannerVisibility = ({
	preferences,
	initialPreferences,
	isInitialRender,
	isPrefetchedPreferencesState,
	ssrOptions,
}: BannerVisibilityHookProps): {
	showBanner: boolean;
	setIsDismissed: (isDismissed: boolean) => void;
} => {
	const isEventSent = useRef(false);
	const [isDismissed, setIsDismissedProp] = useState(false);

	const setIsDismissed = (isDismissed: boolean): void => {
		if (isDismissed) {
			setStrictlyNecessaryCookie(
				ATLASSIAN_BANNER_LAST_DISMISSED_TIME_COOKIE_KEY,
				Date.now().toString(),
				{ path: '/' },
			);
		}
		setIsDismissedProp(isDismissed);
	};

	const showBanner = useMemo(() => {
		return getShowBanner({
			preferences,
			initialPreferences,
			isInitialRender,
			isPrefetchedPreferencesState,
			isDismissed,
			ssrOptions,
		});
	}, [
		isInitialRender,
		initialPreferences,
		isPrefetchedPreferencesState,
		preferences,
		isDismissed,
		ssrOptions,
	]);

	useEffect(() => {
		if (!isEventSent.current && showBanner) {
			sendScreenEvent({ name: 'cookieConsentBanner' });
			isEventSent.current = true;
		}
	}, [isEventSent, showBanner]);

	return { showBanner, setIsDismissed };
};

export const getShowBanner = ({
	preferences,
	initialPreferences,
	isInitialRender,
	isPrefetchedPreferencesState,
	isDismissed,
	ssrOptions,
}: BannerVisibilityHookProps & { isDismissed?: boolean }): boolean => {
	if (getBscGlobalState().localConsentMode) {
		// In V1, we only checked based on the existence of the preferences
		const prefsMissing = !preferences;
		if (ssrOptions?.isSSR ?? isSSR()) {
			// This logic will execute normally in V2, but for now, as SSR prefetching has not been implemented completely
			// just hide the banner if we're missing the data (always will be in SSR-context) and let the UI handle it on rehydration
			return !prefsMissing && !isDismissed;
		}
		return prefsMissing && !isDismissed;
	}
	const shouldShowBanner = calculateBannerStateViaBit(
		isInitialRender,
		initialPreferences,
		ssrOptions,
	);
	const unknownPreferences = !isPrefetchedPreferencesState && !preferences;
	return (unknownPreferences || shouldShowBanner) && !isDismissed;
};

export default useBannerVisibility;
